import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InterestPhase } from "../../lib/realestate";
import { Modal } from "../ui/Modal";

interface Props {
  open: boolean;
  onClose: () => void;
  existing: InterestPhase | null;
  loanStartDate: string;
  onSubmit: (data: {
    startDate: string;
    annualInterestRateBp: number;
    monthlyPaymentCents: number;
    notes: string | null;
  }) => Promise<void>;
  submitting: boolean;
}

function bpToPct(bp: number): string {
  return (bp / 100).toFixed(2);
}

function centsToEurString(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function InterestPhaseFormModal({
  open,
  onClose,
  existing,
  loanStartDate,
  onSubmit,
  submitting,
}: Props) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(existing?.startDate ?? "");
  const [ratePct, setRatePct] = useState(existing ? bpToPct(existing.annualInterestRateBp) : "");
  const [payment, setPayment] = useState(
    existing ? centsToEurString(existing.monthlyPaymentCents) : "",
  );
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStartDate(existing?.startDate ?? "");
    setRatePct(existing ? bpToPct(existing.annualInterestRateBp) : "");
    setPayment(existing ? centsToEurString(existing.monthlyPaymentCents) : "");
    setNotes(existing?.notes ?? "");
    setError(null);
  }, [existing]);

  const title = existing
    ? t("realestate.loan.interest_phase.form_title_edit")
    : t("realestate.loan.interest_phase.form_title_create");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!startDate || !ratePct || !payment) {
      setError(t("realestate.loan.interest_phase.error_required_fields"));
      return;
    }
    if (startDate < loanStartDate) {
      setError(t("realestate.loan.interest_phase.error_phase_start_before_loan"));
      return;
    }
    const rateBp = Math.round(Number.parseFloat(ratePct.replace(",", ".")) * 100);
    const paymentCents = Math.round(Number.parseFloat(payment.replace(",", ".")) * 100);
    if (Number.isNaN(rateBp) || Number.isNaN(paymentCents)) {
      setError(t("realestate.loan.interest_phase.error_invalid_number"));
      return;
    }
    await onSubmit({
      startDate,
      annualInterestRateBp: rateBp,
      monthlyPaymentCents: paymentCents,
      notes: notes.trim() === "" ? null : notes.trim(),
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block">
          <span className="block text-sm font-medium mb-1">
            {t("realestate.loan.interest_phase.start_date")} *
          </span>
          <input
            type="date"
            className="w-full border rounded px-2 py-1"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            min={loanStartDate}
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium mb-1">
            {t("realestate.loan.interest_phase.annual_rate")} *
          </span>
          <input
            type="text"
            inputMode="decimal"
            className="w-full border rounded px-2 py-1"
            value={ratePct}
            onChange={(e) => setRatePct(e.target.value)}
            placeholder="3,50"
            required
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium mb-1">
            {t("realestate.loan.interest_phase.monthly_payment")} *
          </span>
          <input
            type="text"
            inputMode="decimal"
            className="w-full border rounded px-2 py-1"
            value={payment}
            onChange={(e) => setPayment(e.target.value)}
            placeholder="1500,00"
            required
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium mb-1">
            {t("realestate.loan.interest_phase.notes")}
          </span>
          <textarea
            className="w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("realestate.loan.interest_phase.notes_placeholder")}
            maxLength={500}
            rows={2}
          />
        </label>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="px-3 py-1 border rounded"
            onClick={onClose}
            disabled={submitting}
          >
            {t("realestate.loan.interest_phase.form_cancel")}
          </button>
          <button
            type="submit"
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={submitting}
          >
            {t("realestate.loan.interest_phase.form_save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
